import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntilDestroy } from '@ngneat/until-destroy';
import { UtilService } from './shared/services/utils/util.service';
import { IconsService } from './shared/services/utils/icons.service';
import { NavigationService } from './main/navigation.service';
import { SignalrService, SIGNALR_CHANNEL } from './shared/services/signalr/signalr.service';
import { SignalrEvent } from './shared/enums/signalr-event';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { BasicButtonColors, BasicButtonModes } from './shared/enums/buttons';
import { MatDialog } from '@angular/material/dialog';
import { BrowserLocalStorageService } from './shared/services/utils/browserLocalStorage.service';
import { AuthService } from './shared/services/auth/auth.service';
import { ConfigurationService } from './shared/services/utils/configuration.service';
import { FileUploadService } from './shared/services/utils/file-upload.service';
import { DialogData } from './shared/interfaces/dialog.interface';
import { VersionCheckService } from './shared/services/version-check/version-check.service';
import { UserService } from './admin-station/work-center/user/services/user.service';
import { gridsLocalizationMessages, userAllowedLangs } from './admin-station/work-center/user/constants/user-const';
import { loadMessages } from 'devextreme/localization';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'cm-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
@UntilDestroy()
export class AppComponent implements OnInit, OnDestroy {

  private readonly _destroying$ = new Subject<void>();
  public isMobile = false;
  private uploadSubscription: Subscription;

  public isIdleTimeoutKey = 'isIdleTimeout';
  public closeIdleDialog = 'closeIdleDialog';
  public idleState = 'Not started.';
  public setTimedOutRef = null;
  public dialogRef = null;
  public timedOutSeconds = 180;
  public timedOut = false;
  public lastPing?: Date = null;
  private _isLoggingOut = false;

  constructor(
    public dialog: MatDialog,
    public navigationService: NavigationService,
    public authService: AuthService,
    private browserLocalStorageService: BrowserLocalStorageService,
    private configService: ConfigurationService,
    private fileUploadService: FileUploadService,
    private iconsService: IconsService,
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router,
    private signalRService: SignalrService,
    private translate: TranslateService,
    private userService: UserService,
    private utilService: UtilService,
    private versionCheckService: VersionCheckService
  ) {
    this.iconsService.registerIcon();

    loadMessages(gridsLocalizationMessages);
    this.translate.addLangs(userAllowedLangs);
    this.translate.setDefaultLang('en');
  }

  @HostListener('window:resize', [])
  public onResize(): void {
    this.isMobile = UtilService.isMobileAndTabletSize();
  }

  /**
   * Starts authentication process by calling authService.login().
   * When isUserAuthenticated$ it's true, the user is authenticated,
   * we proceed to _postLoginActions() for app initialization.
   */
  public ngOnInit() {

    this.authService.isUserAuthenticated$.subscribe(async (isAuthenticated: boolean) => {
      if (isAuthenticated) {
        await this._postLoginActions();
      }
    });

    this._passwordResetRedirect();

    this.onResize();

    this.authService.login();
  }

  public ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
    this.signalRService.stopCommunication(SIGNALR_CHANNEL);
    this.uploadSubscription.unsubscribe();
    this.versionCheckService.stopVersionCheck();
  }

  private async _initSignalrService() {
    await this.signalRService.createChannel(SIGNALR_CHANNEL);

    if (!this.uploadSubscription) {
      this.signalRService.hubConnections[SIGNALR_CHANNEL].off(SignalrEvent.ImportStatusChanged);
      this.signalRService.hubConnections[SIGNALR_CHANNEL].on(SignalrEvent.ImportStatusChanged, importStatus =>
        this.fileUploadService.onFileProcessing(importStatus)
      );
    }
  }

  private _passwordResetRedirect() {
    //b2c returns an error with a specific code.
    const hash = new URLSearchParams(window.location.hash.substring(1));
    const msError = hash.get('error_description');
    if (msError && msError.includes('AADB2C90118')) {
      window.location.href = this.configService.passwordResetPolicy;
      return;
    }
  }

  /**
   * Perform all the initialization functions that require the user to be authenticated, here!
   */
  private async _postLoginActions(): Promise<void> {
    this.versionCheckService.initVersionCheck();
    this.userService.initCmCloudUser();
    this.userService.userLoginData$.subscribe((userDTO) => {
      if (userDTO?.userId) {
        this.browserLocalStorageService.userID=userDTO.userId;
      }
    });
    this._restoreUserPreferences();
    this._setIdleConfig();
    this._initSignalrService();
  }

  private _restoreUserPreferences(): void {
    const response = this.browserLocalStorageService.getAPILocalStorage();
    this.authService.dbLocalStorage = response;
    const userId = this.userService.getUserLoginData()?.userId;
    this.utilService.setSessionFromPreferences(response, userId);
  }


  // TODO: --------- move all the idle timeout stuff to its own service --------------
  private _setIdleConfig(): void {
    this._setLocalStorageIdleFlag(false);
    this._setCloseIdleDialogFlag(false);
    // sets a wait time for idle timeout, after this time pass it will starts timeout
    this.idle.setIdle(10800);
    // sets a timeout period. after these seconds of inactivity, the user will be considered timed out.
    this.idle.setTimeout(this.timedOutSeconds);
    // sets the default interrupts: [clicks, scrolls, touches to the document...]
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this._onIdleEnd();
    this._onIdleTimeout();
    this._onIdleStart();
    this._onTimeoutWarning();
    this._setKeepAlivePingConfig();

    this.idle.watch();
    this.timedOut = false;
  }

  private _onIdleEnd(): void {
    this.idle.onIdleEnd.subscribe(() => {
      this._setLocalStorageIdleFlag(false);
      this.idleState = 'No longer idle.';
      this._resetIdle();
    });
  }

  private _onIdleTimeout(): void {
    this.idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
    });
  }

  private _onIdleStart(): void {
    this.idle.onIdleStart.subscribe(() => {
      this.idleState = "You've gone idle!";
      this._showIdleDialog();
      this.setTimedOutRef = setTimeout(() => {
        this._setLocalStorageIdleFlag(true);
        this.timedOut = true;
        this._logout();
      }, this.timedOutSeconds * 1000);
    });
  }

  private _onTimeoutWarning(): void {
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!';
    });
  }

  private _setKeepAlivePingConfig(): void {
    this.keepalive.interval(1);
    this.keepalive.onPing.subscribe(() => {
      if (this._getLocalStorageIdleFlag()) {
        this.timedOut = true;
        this._logout();
      } else if (this._getCloseIdleDialogFlag() && this.dialogRef) {
        this.dialogRef.close();
        this._setCloseIdleDialogFlag(false);
      }
      return (this.lastPing = new Date());
    });
  }

  private _resetIdle(): void {
    this._isLoggingOut = false;
    this._setLocalStorageIdleFlag(false);
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  private _logout(): void {
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    this._setLocalStorageIdleFlag(true);
    // Stop the idle watcher and keepalive so no further pings trigger _logout again.
    this.idle.stop();
    try {
      this.authService.logout();
    } catch (error) {
      this.router.navigate(['/']);
    }
  }

  private _setLocalStorageIdleFlag(flag: boolean): void {
    this.browserLocalStorageService.setItem(this.isIdleTimeoutKey, flag.toString());
  }

  private _setCloseIdleDialogFlag(flag: boolean): void {
    this.browserLocalStorageService.setItem(this.closeIdleDialog, flag.toString());
  }

  private _getLocalStorageIdleFlag(): boolean {
    const flag = this.browserLocalStorageService.getItem(this.isIdleTimeoutKey);
    return `${flag}`.toLowerCase() === 'true';
  }

  private _getCloseIdleDialogFlag(): boolean {
    const flag = this.browserLocalStorageService.getItem(this.closeIdleDialog);
    return `${flag}`.toLowerCase() === 'true';
  }

  private _showIdleDialog(): void {
    this.dialogRef = this.dialog.open<ConfirmDialogComponent, DialogData>(ConfirmDialogComponent, {
      panelClass: 'cm-confirm-dialog',
      autoFocus: false,
      data: {
        width: 460,
        title: 'idle-diaglog.title',
        titleAlign: 'justify-content-center',
        message: 'idle-diaglog.message',
        messageAlign: 'text-center',
        primaryAction: {
          label: 'idle-diaglog.continue-session',
          color: BasicButtonColors.INFO,
        },
        tertiaryAction: {
          label: 'idle-diaglog.logout-now',
          mode: BasicButtonModes.FLAT,
        },
        buttonsAlign: 'justify-content-center',
        imgUrl: 'assets/img/idle_image.svg',
        altImg: 'Session Expiring Image',
      },
    });

    this.dialogRef.afterClosed().subscribe((result: string) => {
      clearTimeout(this.setTimedOutRef);
      switch (result) {
        case 'idle-diaglog.logout-now':
          this.timedOut = true;
          this._logout();
          break;
        case 'idle-diaglog.continue-session':
          this._setCloseIdleDialogFlag(true);
          this._resetIdle();
          break;
        default:
          this._setCloseIdleDialogFlag(true);
          this._resetIdle();
          break;
      }
    });
  }
  // -------------------------------------------------------------------------------

}
